// src/lib/pwaUpdate.ts
// Registro único del Service Worker + puente entre virtual:pwa-register y la UI (UpdatePrompt).
import { registerSW } from "virtual:pwa-register";

type Listener = () => void;

let initialized = false;
const listeners = new Set<Listener>();

// Ventana después de abrir la app (o volver a ella) en la que una actualización
// se aplica SOLA, sin preguntar: en ese momento recargar no interrumpe nada.
// Pasada la ventana (la persona ya está usando la app) se muestra el aviso.
// Motivo: muchos tocaban "Luego" y seguían semanas con versiones viejas.
const AUTO_APPLY_WINDOW_MS = 30_000;
const PERIODIC_CHECK_MS = 30 * 60_000;
let lastShownAt = Date.now();

/** ¿La persona está escribiendo algo? (no recargarle la página encima) */
function isEditing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function initPwaUpdate() {
  if (initialized) return;
  initialized = true;

  // Solo usamos esto para la DETECCIÓN ("hay una actualización lista").
  // La función que devuelve registerSW() para aplicarla no se usa — ver
  // el comentario en applyPwaUpdate() más abajo.
  registerSW({
    immediate: true,
    onNeedRefresh() {
      const justOpened = Date.now() - lastShownAt < AUTO_APPLY_WINDOW_MS;
      if (justOpened && !isEditing()) {
        void applyPwaUpdate();
        return;
      }
      listeners.forEach((l) => l());
    },
  });

  // Chequeo proactivo: una PWA instalada en el celu se "usa" abriendo el
  // ícono, no navegando/refrescando como en desktop — el chequeo automático
  // del browser no siempre alcanza a disparar con esa frecuencia. Forzamos
  // una revisión extra cada vez que la app vuelve a primer plano.
  const checkForUpdate = () => {
    navigator.serviceWorker.getRegistration().then((reg) => reg?.update().catch(() => {}));
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      lastShownAt = Date.now(); // volvió a la app: abre una nueva ventana de auto-actualización
      // Si quedó una versión nueva esperando (p. ej. tocó "Luego"), el navegador no
      // vuelve a avisar: la aplicamos ahora que recién vuelve a la app.
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg?.waiting && !isEditing()) void applyPwaUpdate();
        else checkForUpdate();
      });
    }
  });
  window.addEventListener("focus", checkForUpdate);
  // Con la app abierta mucho tiempo (p. ej. en la recepción), revisar igual cada tanto.
  setInterval(checkForUpdate, PERIODIC_CHECK_MS);
}

/** Se suscribe a "hay una actualización lista". Devuelve función de limpieza. */
export function onPwaNeedRefresh(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Activa el SW en espera y recarga la página.
 *
 * OJO: no usamos la función que devuelve registerSW() para esto. Esa función
 * espera el evento "controlling" de workbox-window antes de recargar, pero
 * ese evento solo dispara si el SW llama self.clients.claim() — y acá
 * deliberadamente NO usamos clientsClaim (para que las actualizaciones no
 * pisen sesiones activas sin avisar). Resultado: ese evento nunca llega para
 * la pestaña actual y el reload automático nunca se dispara — el botón queda
 * colgado en silencio. Por eso controlamos el reload nosotros mismos acá,
 * con un timeout de seguridad para que nunca se quede esperando para siempre.
 */
export async function applyPwaUpdate() {
  const reg = await navigator.serviceWorker.getRegistration();
  const waiting = reg?.waiting;

  if (waiting) {
    waiting.postMessage({ type: "SKIP_WAITING" });
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, 1500);
      waiting.addEventListener("statechange", () => {
        if (waiting.state === "activated") {
          clearTimeout(timer);
          resolve();
        }
      });
    });
  }

  window.location.reload();
}
